import { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useConfig } from '../contexts/ConfigContext'
import DatasetPreviewModal from '../components/modals/DatasetPreviewModal'

function DatasetsPage() {
  const [datasets, setDatasets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const [categories, setCategories] = useState([])
  const [previewDataset, setPreviewDataset] = useState(null)

  const { isSingleDistrictMode, getFocusedDistrict } = useConfig()

  // Load datasets registry
  useEffect(() => {
    const loadDatasets = async () => {
      try {
        setLoading(true)
        const response = await fetch('/data/datasets_registry.json')
        if (!response.ok) {
          throw new Error('Failed to load datasets registry')
        }
        const data = await response.json()
        setDatasets(data.datasets || [])
        setCategories(data.categories || [])
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadDatasets()
  }, [])

  // Filter datasets based on current criteria
  const filteredDatasets = useMemo(() => {
    let filtered = datasets.filter(dataset => dataset.published)

    // In single-district mode, auto-filter to focused district
    if (isSingleDistrictMode() && getFocusedDistrict()) {
      const districtName = getFocusedDistrict().split('-')[1]
      filtered = filtered.filter(
        dataset =>
          dataset.district === 'all' ||
          dataset.district === districtName.toLowerCase()
      )
    } else if (selectedDistrict) {
      // In multi-district mode, use selected district filter
      filtered = filtered.filter(
        dataset =>
          dataset.district === selectedDistrict || dataset.district === 'all'
      )
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(
        dataset => dataset.category === selectedCategory
      )
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        dataset =>
          dataset.title.toLowerCase().includes(term) ||
          dataset.description.toLowerCase().includes(term) ||
          dataset.tags.some(tag => tag.toLowerCase().includes(term))
      )
    }

    return filtered
  }, [
    datasets,
    searchTerm,
    selectedCategory,
    selectedDistrict,
    isSingleDistrictMode,
    getFocusedDistrict,
  ])

  // Get unique districts for filter
  const availableDistricts = useMemo(() => {
    const districts = [
      ...new Set(datasets.map(d => d.district).filter(d => d !== 'all')),
    ]
    return districts.map(d => ({
      value: d,
      label: d.charAt(0).toUpperCase() + d.slice(1),
    }))
  }, [datasets])

  const handleDownload = async dataset => {
    try {
      // Fetch the CSV data
      const response = await fetch(dataset.file_path)
      if (!response.ok) {
        throw new Error(`Failed to fetch dataset: ${response.statusText}`)
      }

      let csvText = await response.text()
      let filename = dataset.file_path.split('/').pop()

      // In single-district mode, set appropriate filename
      if (isSingleDistrictMode() && getFocusedDistrict()) {
        const districtName = getFocusedDistrict().split('-')[1]

        // Update filename to indicate filtering
        const nameWithoutExt = filename.replace('.csv', '')
        const today = new Date().toISOString().split('T')[0]
        filename = `${nameWithoutExt}_${districtName}_${today}.csv`
      }

      // Create and trigger download
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      // Download failed - error handled by user notification
      alert(`Download failed: ${err.message}`)
    }
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading datasets...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="animate-fade-in">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Failed to Load Datasets
          </h3>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-surface-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-surface-900 mb-2">
              📊 Available Datasets
            </h1>
            <p className="text-surface-600">
              Download CSV datasets used in dashboard visualizations
            </p>
          </div>

          <div className="text-sm text-surface-600 bg-surface-100 px-3 py-2 rounded-lg font-medium">
            Showing {filteredDatasets.length} of{' '}
            {datasets.filter(d => d.published).length} datasets
          </div>
        </div>

        {/* Filter Controls */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search Input */}
          <div>
            <label
              className="block text-sm font-medium text-surface-700 mb-2"
              htmlFor="search"
            >
              Search datasets
            </label>
            <input
              type="text"
              value={searchTerm}
              id="search"
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by title, description, or tags..."
              className="w-full px-3 py-2 border border-surface-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label
              className="block text-sm font-medium text-surface-700 mb-2"
              htmlFor="category"
            >
              Category
            </label>
            <select
              value={selectedCategory}
              id="category"
              onChange={e => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-surface-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* District Filter (only in multi-district mode) */}
          {!isSingleDistrictMode() && (
            <div>
              <label
                className="block text-sm font-medium text-surface-700 mb-2"
                htmlFor="district"
              >
                District
              </label>
              <select
                id="district"
                value={selectedDistrict}
                onChange={e => setSelectedDistrict(e.target.value)}
                className="w-full px-3 py-2 border border-surface-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All districts</option>
                {availableDistricts.map(district => (
                  <option key={district.value} value={district.value}>
                    {district.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Dataset Cards Grid */}
      {filteredDatasets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-surface-100 p-12 text-center">
          <div className="text-6xl mb-4">📂</div>
          <h3 className="text-lg font-semibold text-surface-900 mb-2">
            No datasets found
          </h3>
          <p className="text-surface-600">
            Try adjusting your search criteria or filters
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredDatasets.map(dataset => (
            <DatasetCard
              key={dataset.id}
              dataset={dataset}
              onDownload={() => handleDownload(dataset)}
              onPreview={() => setPreviewDataset(dataset)}
            />
          ))}
        </div>
      )}

      {/* Dataset Preview Modal */}
      <DatasetPreviewModal
        dataset={previewDataset}
        isOpen={!!previewDataset}
        onClose={() => setPreviewDataset(null)}
        onDownload={() => handleDownload(previewDataset)}
      />
    </div>
  )
}

DatasetCard.propTypes = {
  dataset: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string,
    district: PropTypes.string,
    data_type: PropTypes.string,
    file_size: PropTypes.string,
    record_count: PropTypes.number,
    last_updated: PropTypes.string,
    version: PropTypes.string,
    source: PropTypes.string,
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
}

function DatasetCard({ dataset, onDownload, onPreview }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showReadMore, setShowReadMore] = useState(false)
  const descriptionRef = useRef(null)

  const getDistrictLabel = district => {
    if (district === 'all') return 'All Districts'
    return district.charAt(0).toUpperCase() + district.slice(1)
  }

  // Check if description is long enough to need truncation
  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(
        window.getComputedStyle(descriptionRef.current).lineHeight,
        10
      )
      const maxHeight = lineHeight * 3 // 3 lines
      const actualHeight = descriptionRef.current.scrollHeight
      setShowReadMore(actualHeight > maxHeight)
    }
  }, [dataset.description])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-surface-100 p-6 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div>
            <h3 className="font-semibold text-surface-900 line-clamp-2">
              {dataset.title}
            </h3>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <p
          ref={descriptionRef}
          className={`text-sm text-surface-600 ${!isExpanded ? 'line-clamp-3' : ''}`}
        >
          {dataset.description}
        </p>
        {showReadMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors duration-200"
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>

      {/* Metadata Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-primary-100 text-primary-800">
          {dataset.category}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {getDistrictLabel(dataset.district)}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-green-100 text-green-800">
          {dataset.data_type}
        </span>
      </div>

      {/* File Info */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-xs text-surface-600">
        <div>
          <span className="font-medium">Size:</span> {dataset.file_size}
        </div>
        <div>
          <span className="font-medium">Records:</span>{' '}
          {dataset.record_count?.toLocaleString()}
        </div>
        <div>
          <span className="font-medium">Updated:</span>{' '}
          {new Date(dataset.last_updated).toLocaleDateString()}
        </div>
        <div>
          <span className="font-medium">Version:</span> {dataset.version}
        </div>
      </div>

      {/* Source */}
      {dataset.source && (
        <div className="text-xs text-surface-500 mb-4">
          <span className="font-medium">Source:</span> {dataset.source}
        </div>
      )}

      {/* Actions */}
      <div className="flex space-x-3">
        <button
          onClick={onDownload}
          className="flex-1 bg-primary-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          <span>⬇️</span>
          <span>Download</span>
        </button>
        <button
          onClick={onPreview}
          className="px-4 py-2 border border-surface-300 rounded-lg font-medium text-surface-700 hover:bg-surface-50 transition-colors duration-200"
        >
          Preview
        </button>
      </div>
    </div>
  )
}

export default DatasetsPage
